import React from 'react'
import { Line, LineChart as RechartsLineChart, RectangleProps, ResponsiveContainer, Tooltip, YAxis } from 'recharts'

import { ColorVariant } from '../checkbox'
import ChartTooltip from './ChartTooltip'
import {
  BAR_GAP,
  BAR_WIDTH,
  CHART_HEIGHT,
  CURSOR_STYLE,
  EXTRA_WIDTH,
  LABELS_WIDTH,
  LINE_CHART_LABELS_MARGIN,
  LINE_CHART_MARGINS,
  TICK_STYLE,
  TOOLTIP_WRAPPER_STYLE,
  TRANSPARENT_CURSOR,
} from './constants'
import { ChartProps, LineChartTooltipSort } from './types'
import { useTooltipActiveStateControls } from './useTooltipActiveStateControls'
import { getChartDataItemKey, getColor, tickFormatter } from './utils'

interface CustomCursorProps extends Partial<Omit<RectangleProps, 'points'>> {
  width: number
  points?: [{ x: number; y: number }]
  isActive: boolean
}

const CustomCursor = ({ points, width, isActive }: CustomCursorProps) => {
  const x = points![0].x - width / 2
  return (
    <rect
      x={x}
      width={width}
      height={CHART_HEIGHT}
      strokeWidth={0}
      {...(isActive ? CURSOR_STYLE : TRANSPARENT_CURSOR)}
    />
  )
}

interface Props extends ChartProps {
  tooltipSort?: keyof typeof LineChartTooltipSort
}

export default function LineChart({
  chartData,
  visibleAreas,
  columnsCount,
  barWidth = BAR_WIDTH,
  tooltipSort,
  showTotal,
  isFullWidth,
}: Props) {
  const { isActive, chartWrapperActiveStateControlsProps } = useTooltipActiveStateControls()
  const [activeLineKey, setActiveLineKey] = React.useState('')
  const [reverseTooltipDirection, setReverseTooltipDirection] = React.useState(true)
  const chartWidth = columnsCount * barWidth + (columnsCount - 1) * BAR_GAP + EXTRA_WIDTH

  const ChartWrapper = isFullWidth ? ResponsiveContainer : React.Fragment

  return (
    <div
      {...(isFullWidth ? { className: 'w-full', style: { height: CHART_HEIGHT } } : {})}
      {...chartWrapperActiveStateControlsProps}
    >
      <ChartWrapper>
        <RechartsLineChart
          width={isFullWidth ? barWidth * chartData.length : chartWidth}
          height={CHART_HEIGHT}
          data={chartData}
          margin={isFullWidth ? { ...LINE_CHART_MARGINS, left: barWidth / 2 } : LINE_CHART_MARGINS}
          onMouseMove={(_, mouseEvent) => {
            setReverseTooltipDirection(mouseEvent.clientX > window.innerWidth / 2)
          }}
        >
          <YAxis
            className="font-sans text-muted-2"
            orientation="right"
            tick={TICK_STYLE}
            tickLine={false}
            axisLine={false}
            tickFormatter={tickFormatter}
            interval="preserveStartEnd"
            tickMargin={LINE_CHART_LABELS_MARGIN}
            width={LABELS_WIDTH}
          />
          <Tooltip
            wrapperStyle={TOOLTIP_WRAPPER_STYLE}
            position={{ y: 0 }}
            allowEscapeViewBox={{ x: true }}
            reverseDirection={{ x: reverseTooltipDirection }}
            offset={40}
            isAnimationActive={false}
            cursor={<CustomCursor width={barWidth} isActive={isActive} />}
            content={({ payload }) => (
              <ChartTooltip isActive={isActive} payload={payload} payloadSort={tooltipSort} activeKey={activeLineKey} />
            )}
          />
          {showTotal && (
            <Line
              name="Total"
              type="linear"
              dot={{ strokeWidth: 0 }}
              activeDot={{
                strokeWidth: 1,
                stroke: getColor(ColorVariant.gray),
                onMouseEnter: () => {
                  setActiveLineKey('Total')
                },
                onMouseLeave: () => {
                  setActiveLineKey('')
                },
              }}
              dataKey="total"
              stroke={getColor(ColorVariant.gray)}
              fill={getColor(ColorVariant.gray)}
              isAnimationActive={false}
            />
          )}
          {visibleAreas.map((area) => {
            const itemKey = getChartDataItemKey(area)
            return (
              <Line
                key={itemKey}
                name={area.label}
                type="linear"
                strokeWidth={area.isHighlighted ? 2 : 1}
                dot={{ strokeWidth: area.isHighlighted ? 2 : 0 }}
                activeDot={{
                  strokeWidth: area.isHighlighted ? 3 : 1,
                  stroke: area.color,
                  onMouseEnter: () => {
                    setActiveLineKey(itemKey)
                  },
                  onMouseLeave: () => {
                    setActiveLineKey('')
                  },
                }}
                dataKey={itemKey}
                stroke={area.color}
                fill={area.color}
                isAnimationActive={false}
              />
            )
          })}
        </RechartsLineChart>
      </ChartWrapper>
    </div>
  )
}
